<template>
  <div class="page-picker">
    <ui-icon
      class="ml-8 ml-first-0 hidden-xs"
      :color="$colors.green"
      :class="{
        activeButton:!isDisabledLeft
      }"
      :disabled="isDisabledLeft"
      @click="goToFirst"
      icon="fas fa-chevron-double-left"
    />

    <ui-icon
      class="ml-8 ml-first-0"
      :disabled="isDisabledLeft"
      :class="{
        activeButton:!isDisabledLeft
      }"
      :color="$colors.green"
      @click="goToPrev"
      icon="fas fa-chevron-left"
    />

    <ui-input
      ref="inputRef"
      v-model="currentPage"
      class="page-picker__input ml-8 ml-first-0"
      :hide-details="1"
      @blur="setPage(false)"
      @keyup.enter="setPage(true)"
    />

    <div class="page-picker__text ml-4 mr-8"> {{ t('of {0}', [ lastPageNumber ]) }} </div>

    <ui-icon
      class="ml-first-0"
      :disabled="isDisabledRight"
      :class="{
        activeButton:!isDisabledRight
      }"
      :color="$colors.green"
      @click="goToNext"
      icon="fas fa-chevron-right"
    />

    <ui-icon
      class="ml-8 ml-first-0 hidden-xs"
      :disabled="isDisabledRight"
      :class="{
        activeButton:!isDisabledRight
      }"
      :color="$colors.green"
      @click="goToLast"
      icon="fas fa-chevron-double-right"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import query from '@/components/query/query-constructor';
import { useI18n } from '@/composables/useI18n';
import { useColors } from '@/composables/useColors';
import { useUrl } from '@/composables/useUrl';
import { useTableQuery } from '@/composables/useTableQuery';
import { usePickerPushUrl } from '@/composables/usePickerPushUrl';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';

const { t } = useI18n();
const $colors = useColors();
const route = useRoute();
const store = useStore();
const { isReport, currentReportType } = useUrl();
const tableQueryApi = useTableQuery();
const { pushReportUrl } = usePickerPushUrl();

const inputRef = ref<{ blur?: () => void } | null>(null);
const currentPage = ref<number | string>(1);

const getReportTypeKey = () => {
  const reportType = currentReportType.value;
  return Array.isArray(reportType) ? reportType[0] : reportType;
};

const getCurrentOffset = () => {
  const { offset } = tableQueryApi.tableQueryForCurrentTab.value as {
    offset?: number | Record<string, number>;
  };
  if (typeof offset === 'number') {
    return offset;
  }
  const reportType = getReportTypeKey();
  if (offset && reportType) {
    return offset[reportType] ?? 0;
  }
  return 0;
};

const normalizedValue = computed(() => {
  const { limit } = tableQueryApi.tableQueryForCurrentTab.value as {
    limit?: number;
  };
  return Math.ceil(getCurrentOffset() / (limit || 1)) + 1;
});

const lastPageNumber = computed(() => {
  const { limit } = tableQueryApi.tableQueryForCurrentTab.value as { limit?: number };
  const totalItems = tableQueryApi.tableTotal.value;
  return Math.ceil(totalItems / (limit || 1)) || 1;
});

const isDisabledLeft = computed(() => {
  const { limit } = tableQueryApi.tableQueryForCurrentTab.value as { limit?: number };
  if (!limit) return true;
  return normalizedValue.value <= 1;
});

const isDisabledRight = computed(() => {
  const { limit } = tableQueryApi.tableQueryForCurrentTab.value as { limit?: number };
  if (!limit) return true;
  return normalizedValue.value === lastPageNumber.value;
});

const getFixedPage = () => {
  const page = parseInt(String(currentPage.value), 10);
  if (Number.isNaN(page) || Number(currentPage.value) < 1) {
    return 1;
  }
  if (page > lastPageNumber.value) {
    return lastPageNumber.value;
  }
  return page;
};

const setPageFromOffset = () => {
  const { limit } = tableQueryApi.tableQueryForCurrentTab.value as {
    limit?: number;
  };
  currentPage.value = Math.floor(getCurrentOffset() / (limit || 1)) + 1;
};

const saveOffset = ({ value, isPushUrl }: { value: number; isPushUrl: boolean }) => {
  const reportType = getReportTypeKey();
  if (reportType) {
    tableQueryApi.patchTableOffsetForCurrentTab({
      groupBy: reportType,
      value,
    });
  }
  if (isPushUrl) {
    store.setTableLoading(true);
    $emitter.emit('set-report-loading', true);
    pushReportUrl(() => {
      $emitter.emit('reload-report');
    });
  }
};

const setOffsetFromPage = (pageValue: number | string = currentPage.value) => {
  const { limit } = tableQueryApi.tableQueryForCurrentTab.value as { limit?: number };
  const newOffset = (Number(pageValue) - 1) * (limit || 0);
  saveOffset({ value: newOffset, isPushUrl: true });
};

const setPage = (blur = false) => {
  const page = getFixedPage();
  if (page !== currentPage.value) {
    currentPage.value = page;
    setOffsetFromPage();
  }
  if (blur) {
    inputRef.value?.blur?.();
  }
};

const goToNext = () => {
  const page = getFixedPage();
  if (page < lastPageNumber.value) {
    currentPage.value = page + 1;
  }
  setOffsetFromPage();
};

const goToPrev = () => {
  const page = getFixedPage();
  if (page > 1) {
    currentPage.value = page - 1;
  }
  setOffsetFromPage();
};

const goToFirst = () => {
  currentPage.value = 1;
  setOffsetFromPage();
};

const goToLast = () => {
  currentPage.value = lastPageNumber.value;
  setOffsetFromPage();
};

const resetPage = () => {
  currentPage.value = 1;
  setOffsetFromPage();
};

const onBusApplyFilters = () => {
  resetPage();
};

const onBusResetOffset = () => {
  resetPage();
};

const applyDataFromStore = () => {
  setPageFromOffset();
};

const applyDataFromUrl = () => {
  const routeParams = query.decode(route.params.data as string) || {};
  const { offset } = routeParams;
  saveOffset({
    value: offset ? +offset : 0,
    isPushUrl: false,
  });
  setPageFromOffset();
};

const onBusReportTabAdded = () => {
  if (!isReport.value) return;

  currentPage.value = 1;
  store.setTableTotal(0);

  if (getCurrentOffset()) {
    applyDataFromStore();
  } else {
    applyDataFromUrl();
  }
};

const resetPagingForTabChange = () => {
  currentPage.value = 1;
  store.setTableTotal(0);
};

const onBusReportTabUpdated = () => {
  if (!isReport.value) return;

  resetPagingForTabChange();

  if (!getCurrentOffset()) {
    saveOffset({ value: 0, isPushUrl: true });
    return;
  }
};

watch(
  () => tableQueryApi.tableActiveTab.value,
  () => {
    if (!isReport.value) return;
    resetPagingForTabChange();
  },
);

watch(
  () => tableQueryApi.tableTotal.value,
  () => {
    if (!tableQueryApi.tableTotal.value) return;
    setPageFromOffset();
    if (Number(currentPage.value) > lastPageNumber.value) {
      currentPage.value = lastPageNumber.value;
      setOffsetFromPage();
    }
  },
);

onMounted(() => {
  $emitter.on('reset-pagination', resetPage);
  $emitter.on('apply-filters', onBusApplyFilters);
  $emitter.on('reset-offset', onBusResetOffset);
  $emitter.on('report-tab-added', onBusReportTabAdded);
  $emitter.on('report-tab-updated', onBusReportTabUpdated);

  if (isReport.value) {
    applyDataFromStore();
    return;
  }
  if (getCurrentOffset()) {
    applyDataFromStore();
  } else {
    applyDataFromUrl();
  }
});

onBeforeUnmount(() => {
  $emitter.off('reset-pagination', resetPage);
  $emitter.off('apply-filters', onBusApplyFilters);
  $emitter.off('reset-offset', onBusResetOffset);
  $emitter.off('report-tab-added', onBusReportTabAdded);
  $emitter.off('report-tab-updated', onBusReportTabUpdated);
});
</script>

<style lang="scss" scoped>
.activeButton{
  cursor: pointer;
}
</style>

<style lang="scss">
.page-picker {
  display: flex;
  align-items: center;
  min-height: 32px;

  &__text {
    white-space: nowrap;
    font-size: 12px;
    line-height: 1.33;
    color: var(--color-graphite);
    padding-bottom: 1px;
    margin-top: 2px;
  }

  &__input {
    width: 40px;
    height: 32px;

    &.ui-input__wrapper .ui-input.ui-input--text.v-text-field input,
    &.ui-input__wrapper .ui-input input {
      text-align: center;
      padding-left: 4px;
      padding-right: 4px;
      color: var(--color-graphite);
      font-size: 12px;
      line-height: 1.33;
    }
  }
}
</style>
